import { Temporal } from '@js-temporal/polyfill'
import { v4 as uuid } from 'uuid'
import * as z from 'zod'

import type { Service, Tag as TagType } from '~publish/gql/graphql'
import { AttachmentTypes, Services } from '~publish/legacy/constants'

import type { ListItem } from '../PostItems'

export type ValidHeaders = 'Text' | 'Tags' | 'Image URL' | 'Posting Time'

// Shape produced after validating a CSV line
export type CsvLine = {
  [key in ValidHeaders]?: string
}

export type BulkPostSchemaOptions = {
  charLimit?: number
  requireText?: boolean
  requireImageUrl?: boolean
  requireTextOrImage?: boolean
}

export const createCsvLineSchema = ({
  charLimit = 500,
  requireText = false,
  requireImageUrl = false,
  requireTextOrImage = true,
}: BulkPostSchemaOptions = {}): z.ZodType<CsvLine, z.ZodTypeDef, unknown> => {
  const preprocessEmpty = (v: unknown): unknown =>
    typeof v === 'string' && v.trim() === '' ? undefined : v

  // TEXT field
  const textSchemaRequired = z
    .string()
    .trim()
    .max(charLimit, `Text must not exceed ${charLimit} characters`)

  const textSchemaOptional = z
    .string()
    .trim()
    .max(charLimit, `Text must not exceed ${charLimit} characters`)

  const text = requireText
    ? z.preprocess(preprocessEmpty, textSchemaRequired)
    : z.preprocess(preprocessEmpty, textSchemaOptional.optional())

  // IMAGE URL field
  const imageSchemaRequired = z.string().trim().url('Invalid image URL format')

  const imageSchemaOptional = z.string().trim().url('Invalid image URL format')

  const image = requireImageUrl
    ? z.preprocess(preprocessEmpty, imageSchemaRequired)
    : z.preprocess(preprocessEmpty, imageSchemaOptional.optional())

  const postingTimeSchema = z
    .string()
    .trim()
    .optional()
    .default('Next Available')

  const schema = z.object({
    Text: text,
    'Image URL': image,
    Tags: z.preprocess(
      (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
      z.string().optional(),
    ),
    'Posting Time': postingTimeSchema,
  })

  // ensure at least one when requested
  return requireTextOrImage
    ? schema.refine(
        (data) => Boolean(data.Text) || Boolean(data['Image URL']),
        { message: 'Either Text or Image URL must be provided' },
      )
    : schema
}

// You can still get type inference by using the schema from the function
export type CSVLine = z.infer<ReturnType<typeof createCsvLineSchema>>

const getValidationOptions = (
  channelService: Service,
): BulkPostSchemaOptions => {
  const service = Services.get(channelService)
  const charLimit =
    typeof service?.charLimit === 'function'
      ? service.charLimit(undefined)
      : service?.charLimit
  const requireText = service?.requiresText ?? false
  const requireImageUrl =
    service?.requiredAttachmentType === AttachmentTypes.MEDIA
  return {
    charLimit: charLimit ?? 500,
    requireText,
    requireImageUrl,
    requireTextOrImage: !requireText && !requireImageUrl,
  }
}

const parseTags = (
  importedTags: string | undefined,
  existingTags: TagType[],
): { tags: TagType[]; errors: string[] } => {
  const rowTags = importedTags
    ? importedTags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    : []
  const existingTagMap = new Map(existingTags.map((tag) => [tag.name, tag]))

  const { actualTags, missingTags } = rowTags.reduce<{
    actualTags: TagType[]
    missingTags: string[]
  }>(
    (acc, tagName) => {
      const found = existingTagMap.get(tagName)
      if (found) {
        acc.actualTags.push(found)
      } else {
        acc.missingTags.push(tagName)
      }
      return acc
    },
    { actualTags: [], missingTags: [] },
  )

  const errors: string[] = []
  if (missingTags.length > 0) {
    errors.push(
      `Could not find tag${
        missingTags.length > 1 ? 's' : ''
      }: ${missingTags.join(', ')}`,
    )
  }
  return { tags: actualTags, errors }
}

/**
 * Validate a posting time string in "YYYY-MM-DD HH:MM" format
 * Returns an error message if the posting time is invalid
 * Returns undefined if the posting time is valid
 */
export const validatePostingTime = (
  postingTime: string,
  timezone: string,
): string | undefined => {
  if (postingTime === 'Prioritize' || postingTime === 'Next Available') {
    return undefined
  }

  const match = postingTime.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/)
  if (!match) {
    return 'Invalid Posting Time format. Please use YYYY-MM-DD HH:MM (24-hour format).'
  }

  const [_, year, month, day, hour, minute] = match

  // Month-aware day validation
  const yearNum = Number(year)
  const monthNum = Number(month)
  const dayNum = Number(day)

  if (monthNum < 1 || monthNum > 12) {
    return `Please specify a month between 01-12`
  }

  // Days in each month, index 1-12
  const daysInMonth = [
    0,
    31,
    yearNum % 4 === 0 && (yearNum % 100 !== 0 || yearNum % 400 === 0) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ]
  if (dayNum < 1 || dayNum > daysInMonth[monthNum]) {
    return `Please specify a day between 01-${daysInMonth[monthNum]}`
  }

  if (Number(hour) < 0 || Number(hour) > 23) {
    return `Please specify an hour between 00-23`
  }

  if (Number(minute) < 0 || Number(minute) > 59) {
    return `Please specify minutes between 00-59`
  }

  try {
    const isoString = postingTime.replace(/\s+/, 'T')
    const plainDateTime = Temporal.PlainDateTime.from(isoString)
    const zonedDateTime = plainDateTime.toZonedDateTime(timezone)
    const zonedNow = Temporal.Now.zonedDateTimeISO(timezone)
    if (zonedDateTime.epochSeconds < zonedNow.epochSeconds) {
      return 'Posting Time must be in the future'
    }
    return undefined
  } catch (_) {
    return 'Invalid Posting Time format. Please use YYYY-MM-DD HH:MM (24-hour format).'
  }
}

export const validateAndConstructItem = ({
  row,
  existingTags = [],
  channelId,
  channelService,
  channelTimezone,
}: {
  row: CSVLine
  existingTags: TagType[]
  channelId: string
  channelService: Service
  channelTimezone: string
}): ListItem => {
  const validationSchema = createCsvLineSchema(
    getValidationOptions(channelService),
  )
  const validationResult = validationSchema.safeParse(row)
  const success = validationResult.success
  const errorMessages =
    validationResult.success === false
      ? validationResult.error.issues.map((issue) => issue.message)
      : []

  const { tags: validTags, errors: tagErrors } = parseTags(
    row.Tags,
    existingTags,
  )

  errorMessages.push(...tagErrors)

  // Store the posting time string for display and later parsing
  const postingTime =
    row['Posting Time'] === '' || row['Posting Time'] === undefined
      ? 'Next Available'
      : row['Posting Time']
  const postingTimeError = validatePostingTime(postingTime, channelTimezone)
  if (postingTimeError) {
    errorMessages.push(postingTimeError)
  }

  // Check if there are any errors (validation errors or missing tags)
  const hasErrors = !success || errorMessages.length > 0

  return {
    id: uuid(),
    data: {
      profile_ids: [channelId],
      text: row.Text,
      media: row['Image URL'] ? { photo: row['Image URL'] } : undefined,
      tags: validTags,
      // Always set created_source to bulk_upload here for BE Post Created tracking
      created_source: 'bulk_upload' as const,
      postingTime,
    },
    status: hasErrors ? 'error' : 'valid',
    errors: errorMessages,
  }
}
